/**
 * CollisionVolumeFactory
 * Creates collision volumes (OBBs and BVH trees) for collision detection
 * Ported from demo/src/utils/collision.ts
 */

import * as THREE from 'three'
import { OBB } from 'three/examples/jsm/math/OBB.js'
import { ConvexGeometry } from 'three/examples/jsm/geometries/ConvexGeometry.js'
import { computeBoundsTree, disposeBoundsTree, acceleratedRaycast } from 'three-mesh-bvh'
import {
  VERTEX_SAMPLE_RATE,
  VERTEX_SAMPLE_RATE_COMPONENTS,
  SCALED_BOUNDING_BOX_FACTOR,
  MAJOR_MESH_VERTEX_THRESHOLD,
  MINOR_MESH_VERTEX_THRESHOLD,
  ENABLE_COLLISION_DEBUG,
  DEBUG_VERBOSITY_LEVEL,
  DEBUG_OBB_CREATION,
  DEBUG_COMPONENT_CLASSIFICATION,
  DEBUG_CONVEX_HULL_FAILURES,
  DEBUG_LOW_EFFICIENCY_THRESHOLD,
} from '@/constants/collision'
import type { CollisionDebugFlags, DebugVerbosityLevel } from '@/types/collision'

// Extend Three.js prototypes for BVH support
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree
THREE.Mesh.prototype.raycast = acceleratedRaycast

// Runtime collision debug state
let collisionDebugEnabled = ENABLE_COLLISION_DEBUG
const debugVerbosity: DebugVerbosityLevel = DEBUG_VERBOSITY_LEVEL as DebugVerbosityLevel
const debugFlags: CollisionDebugFlags = {
  OBB_CREATION: DEBUG_OBB_CREATION,
  COMPONENT: DEBUG_COMPONENT_CLASSIFICATION,
  OBB_INTERSECTION: true,
  BVH: true,
  WALL: true,
  HULL_FAILURE: DEBUG_CONVEX_HULL_FAILURES,
}

// Cache for hull efficiency logs to prevent spam
const hullEfficiencyLogCache = new Set<string>()

// Cache for base OBBs (computed once per aircraft type)
const baseOBBCache = new Map<string, OBB>()

// Track which aircraft types have had BVH computed
const bvhProcessed = new Set<string>()

/**
 * Enable collision debug logging at runtime
 */
export function enableCollisionDebug(): void {
  collisionDebugEnabled = true
  console.log('✅ Collision debug logging enabled')
}

/**
 * Disable collision debug logging at runtime
 */
export function disableCollisionDebug(): void {
  collisionDebugEnabled = false
  console.log('❌ Collision debug logging disabled')
}

// Register on window for dev tools access
if (typeof window !== 'undefined') {
  ;(window as unknown as Record<string, unknown>).enableCollisionDebug = enableCollisionDebug
  ;(window as unknown as Record<string, unknown>).disableCollisionDebug = disableCollisionDebug
}

/**
 * Debug logging utility for collision detection
 */
function debugLog(
  category: keyof CollisionDebugFlags,
  message: string,
  data?: unknown
): void {
  if (!collisionDebugEnabled) return

  if (debugFlags[category]) {
    const prefix: Record<keyof CollisionDebugFlags, string> = {
      OBB_CREATION: '📦',
      COMPONENT: '🔧',
      OBB_INTERSECTION: '🎯',
      BVH: '🔬',
      WALL: '🧱',
      HULL_FAILURE: '⚠️',
    }

    if (data && debugVerbosity === 'VERBOSE') {
      console.log(`${prefix[category]} [COLLISION DEBUG ${category}] ${message}`, data)
    } else {
      console.log(`${prefix[category]} [COLLISION DEBUG ${category}] ${message}`)
    }
  }
}

/**
 * Create an OBB from a Three.js object's bounding box
 */
export function createOBBFromObject(object: THREE.Object3D): OBB {
  const box = new THREE.Box3().setFromObject(object)
  const obb = new OBB()
  obb.fromBox3(box)
  return obb
}

/**
 * Get or create a base OBB for an aircraft type (cached)
 */
export function getBaseOBB(scene: THREE.Object3D, typeId: string): OBB {
  if (!baseOBBCache.has(typeId)) {
    const baseOBB = createOBBFromObject(scene)
    baseOBBCache.set(typeId, baseOBB)
  }
  return baseOBBCache.get(typeId)!.clone()
}

/**
 * Create a fresh OBB from the object's current world-transformed state
 */
export function createOBBFromWorldTransform(object: THREE.Object3D): OBB {
  object.updateMatrixWorld(true)

  const box = new THREE.Box3().setFromObject(object)
  const obb = new OBB()
  obb.fromBox3(box)

  return obb
}

/**
 * Create a single OBB from sampled convex hull for more precise collision detection
 */
export function createConvexHullOBB(object: THREE.Object3D): OBB {
  object.updateMatrixWorld(true)

  try {
    // Collect sampled vertices from all meshes in the aircraft
    const sampledVertices: THREE.Vector3[] = []

    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mesh = child as THREE.Mesh
        const geometry = mesh.geometry

        if (geometry.attributes.position) {
          const positions = geometry.attributes.position
          const matrix = mesh.matrixWorld

          // Sample vertices instead of using all for tighter hull
          for (let i = 0; i < positions.count; i += VERTEX_SAMPLE_RATE) {
            const vertex = new THREE.Vector3(
              positions.getX(i),
              positions.getY(i),
              positions.getZ(i)
            )

            // Transform to world coordinates
            vertex.applyMatrix4(matrix)
            sampledVertices.push(vertex)
          }
        }
      }
    })

    if (sampledVertices.length === 0) {
      return createOBBFromWorldTransform(object)
    }

    // Create convex hull from sampled vertices
    const convexGeometry = new ConvexGeometry(sampledVertices)

    // Create bounding box from convex hull
    convexGeometry.computeBoundingBox()
    const hullBox = convexGeometry.boundingBox!

    // Get original bounding box for comparison
    const originalBox = new THREE.Box3().setFromObject(object)

    // Log hull efficiency
    if (import.meta.env.DEV) {
      const hullVolume = hullBox.getSize(new THREE.Vector3()).lengthSq()
      const originalVolume = originalBox.getSize(new THREE.Vector3()).lengthSq()
      const efficiency = (((originalVolume - hullVolume) / originalVolume) * 100).toFixed(1)

      const efficiencyKey = `hull_${sampledVertices.length}_${efficiency}`

      if (!hullEfficiencyLogCache.has(efficiencyKey)) {
        hullEfficiencyLogCache.add(efficiencyKey)
        console.log(
          `📐 Hull Efficiency: ${efficiency}% tighter than bounding box (${sampledVertices.length} vertices sampled)`
        )

        debugLog(
          'OBB_CREATION',
          `Single hull OBB created: ${sampledVertices.length} vertices (1 of ${VERTEX_SAMPLE_RATE}), efficiency: ${efficiency}%`
        )

        if (parseFloat(efficiency) < DEBUG_LOW_EFFICIENCY_THRESHOLD * 100) {
          debugLog(
            'OBB_CREATION',
            `⚠️ LOW EFFICIENCY HULL: ${efficiency}% (threshold: ${DEBUG_LOW_EFFICIENCY_THRESHOLD * 100}%) - May cause false positives`
          )
        }
      }
    }

    // Create OBB from the convex hull bounding box
    const obb = new OBB()
    obb.fromBox3(hullBox)

    return obb
  } catch (error) {
    console.warn('Convex hull generation failed, using scaled bounding box:', error)

    debugLog(
      'HULL_FAILURE',
      `Convex hull generation FAILED - using ${SCALED_BOUNDING_BOX_FACTOR * 100}% scaled bounding box fallback. May be less accurate.`,
      { error: error instanceof Error ? error.message : String(error) }
    )

    return createScaledBoundingBoxOBB(object, SCALED_BOUNDING_BOX_FACTOR)
  }
}

/**
 * Create multiple component-based convex hulls for maximum accuracy
 */
export function createMultiComponentOBBs(object: THREE.Object3D): OBB[] {
  object.updateMatrixWorld(true)

  const componentOBBs: OBB[] = []

  try {
    // Group meshes by importance and create separate hulls
    const majorMeshes: THREE.Mesh[] = []
    const minorMeshes: THREE.Mesh[] = []

    object.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        const mesh = child as THREE.Mesh
        const geometry = mesh.geometry

        if (geometry.attributes.position) {
          const vertexCount = geometry.attributes.position.count

          // Separate major components (body, wings) from minor ones (details)
          if (
            vertexCount > MAJOR_MESH_VERTEX_THRESHOLD ||
            mesh.name.toLowerCase().includes('body') ||
            mesh.name.toLowerCase().includes('wing') ||
            mesh.name.toLowerCase().includes('fuselage')
          ) {
            majorMeshes.push(mesh)

            debugLog(
              'COMPONENT',
              `Major component detected: "${mesh.name}" (${vertexCount.toLocaleString()} vertices)`
            )
          } else if (vertexCount > MINOR_MESH_VERTEX_THRESHOLD) {
            minorMeshes.push(mesh)

            debugLog(
              'COMPONENT',
              `Minor component detected: "${mesh.name}" (${vertexCount.toLocaleString()} vertices)`
            )
          }
        }
      }
    })

    debugLog(
      'COMPONENT',
      `Multi-component classification: ${majorMeshes.length} major, ${minorMeshes.length} minor meshes`
    )

    // Create convex hulls for major components
    for (const mesh of majorMeshes) {
      const vertices: THREE.Vector3[] = []
      const geometry = mesh.geometry
      const positions = geometry.attributes.position
      const matrix = mesh.matrixWorld

      // Sample vertices from this component
      for (let i = 0; i < positions.count; i += VERTEX_SAMPLE_RATE_COMPONENTS) {
        const vertex = new THREE.Vector3(
          positions.getX(i),
          positions.getY(i),
          positions.getZ(i)
        )
        vertex.applyMatrix4(matrix)
        vertices.push(vertex)
      }

      if (vertices.length > 4) {
        // Need at least 4 points for convex hull
        const convexGeometry = new ConvexGeometry(vertices)
        convexGeometry.computeBoundingBox()
        const obb = new OBB()
        obb.fromBox3(convexGeometry.boundingBox!)
        componentOBBs.push(obb)
      }
    }

    // Group minor meshes into a single hull if they exist
    if (minorMeshes.length > 0) {
      const combinedVertices: THREE.Vector3[] = []

      for (const mesh of minorMeshes) {
        const geometry = mesh.geometry
        const positions = geometry.attributes.position
        const matrix = mesh.matrixWorld

        // Sample fewer vertices from minor components
        for (let i = 0; i < positions.count; i += VERTEX_SAMPLE_RATE_COMPONENTS * 2) {
          const vertex = new THREE.Vector3(
            positions.getX(i),
            positions.getY(i),
            positions.getZ(i)
          )
          vertex.applyMatrix4(matrix)
          combinedVertices.push(vertex)
        }
      }

      if (combinedVertices.length > 4) {
        const convexGeometry = new ConvexGeometry(combinedVertices)
        convexGeometry.computeBoundingBox()
        const obb = new OBB()
        obb.fromBox3(convexGeometry.boundingBox!)
        componentOBBs.push(obb)
      }
    }

    return componentOBBs
  } catch (error) {
    console.warn('Multi-component hull generation failed, using single hull:', error)
    return [createConvexHullOBB(object)]
  }
}

/**
 * Create a scaled bounding box OBB to reduce false positives
 */
export function createScaledBoundingBoxOBB(
  object: THREE.Object3D,
  scale: number = SCALED_BOUNDING_BOX_FACTOR
): OBB {
  object.updateMatrixWorld(true)

  const box = new THREE.Box3().setFromObject(object)
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())

  // Scale down the box to reduce false positives
  size.multiplyScalar(scale)

  // Create new scaled box
  const scaledBox = new THREE.Box3()
  scaledBox.setFromCenterAndSize(center, size)

  const obb = new OBB()
  obb.fromBox3(scaledBox)

  return obb
}

/**
 * Ensure BVH trees are computed for all meshes in an object
 */
export function ensureBVHForObject(object: THREE.Object3D, typeId: string): void {
  let meshCount = 0

  object.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const mesh = child as THREE.Mesh
      const geometry = mesh.geometry

      if (geometry.attributes.position) {
        // Compute BVH tree for this mesh geometry if not already computed
        if (!geometry.boundsTree) {
          geometry.computeBoundsTree()
        }
        meshCount++
      }
    }
  })

  // Log only once per aircraft type
  if (import.meta.env.DEV && !bvhProcessed.has(typeId)) {
    bvhProcessed.add(typeId)
    console.log(`🌳 BVH: Computed bounds trees for ${typeId} (${meshCount} meshes)`)
  }
}

/**
 * Determine if an aircraft should use multi-component collision detection
 */
export function shouldUseMultiComponent(object: THREE.Object3D): boolean {
  let hasNamedComponents = false
  let majorMeshCount = 0

  object.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      const mesh = child as THREE.Mesh
      const name = mesh.name.toLowerCase()

      // Check for named aircraft parts
      if (
        name.includes('body') ||
        name.includes('wing') ||
        name.includes('fuselage') ||
        name.includes('engine')
      ) {
        hasNamedComponents = true
      }

      // Count major meshes
      if (mesh.geometry?.attributes?.position?.count > MAJOR_MESH_VERTEX_THRESHOLD) {
        majorMeshCount++
      }
    }
  })

  // Use multi-component if we have named parts or multiple major meshes
  return hasNamedComponents || majorMeshCount > 1
}

/**
 * Clear the OBB cache (useful when aircraft models are reloaded)
 */
export function clearOBBCache(): void {
  baseOBBCache.clear()
  bvhProcessed.clear()
  hullEfficiencyLogCache.clear()
}
